import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { HighchartsSeverity } from '../../services/highcharts/types';
import { Subscription } from 'rxjs';
import { isNil, round } from 'lodash';

@Component({
  selector: 'nrwl-stream-metrics-chart',
  templateUrl: './stream-metrics-chart.component.html',
  styleUrls: ['./stream-metrics-chart.component.scss']
})
export class StreamMetricsChartComponent implements OnInit, OnDestroy {
  @Input() seriesOptions;
  @Input() chartData: any;
  @Input() stepTime: any = 1;
  @Input() streamConfig: any;
  @Input() setInterval = true;
  @Input() showCurrentValue = true;

  metricsName: string;
  chartOptions: Highcharts.Options;
  dataSource: any = [];
  dsSubscription: Subscription;
  metricsCurrentValue: number;
  metricsUnit: string;
  currentValueStatus: boolean;
  zones = [];
  data = [];
  thresholdData = [];
  seriesData = [];
  isMetricsCurrentValueValid: boolean;
  maxAllowedGap = 1;
  aggregationRange = 60000;
  currentValuelabel = '(current)';
  constructor() {}

  ngOnInit(): void {
    this.metricsName = this.seriesOptions.name;
    this.chartOptions = this.getOptions();
    this.currentValuelabel =
      this.seriesOptions.field === 'frame-loss-ratio' ? '(avg)' : '(current)';
    this.dsSubscription = this.chartData.subscribe((dataItem: any) => {
      if (dataItem) {
        this.getSeriesData(dataItem);
        this.dataSource = JSON.parse(JSON.stringify(this.seriesData));
        if (!this.dataSource.length) {
          this.chartOptions = this.getOptions();
        }
        const itemLength: number = dataItem?.length - 1;
        if (itemLength >= 0) {
          const currentValue = dataItem[itemLength]?.data[this.seriesOptions.field];
          this.metricsCurrentValue = !isNaN(currentValue)
            ? parseFloat(currentValue) < 0
              ? 0
              : round(parseFloat(currentValue), 3)
            : currentValue;
          this.isMetricsCurrentValueValid = !isNil(this.metricsCurrentValue);
        }
        const len: number = this.zones?.length - 1;
        if (len >= 0) {
          this.currentValueStatus = this.zones[len].thresholdReached;
        }
        if (this.seriesOptions.unit) {
          this.metricsUnit = this.seriesOptions?.unit;
        }
      }
    });
  }

  ngOnDestroy() {
    this.dsSubscription.unsubscribe();
  }

  getSeriesOptions() {
    const options = {
      type: 'line',
      zoneAxis: 'y',
      zones: this.zones,
      lineColor: HighchartsSeverity.OK,
      fillColor: {
        linearGradient: {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 1
        },
        stops: [
          [0, HighchartsSeverity.OK],
          [0.9, '#2DA0A510']
        ]
      }
    };
    return [{ ...options, ...this.seriesOptions }];
  }

  getOptions(): Highcharts.Options {
    const noDataMsg = this.dataSource.length > 0 ? 'Collecting data...' : 'No data found';
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const chartOptions: Highcharts.Options = {
      time: {
        useUTC: false
      },
      title: {
        text: ''
      },
      series: this.getSeriesOptions(),
      plotOptions: {
        line: {
          lineWidth: 2
        },
        area: {
          stacking: 'normal',
          lineWidth: 2
        },
        areaspline: {
          stacking: 'normal',
          lineWidth: 2
        },
        series: {
          marker: {
            enabled: false
          },
          clip: false,
          connectNulls: false
        }
      },
      credits: { enabled: false },
      legend: { 
        enabled: true,
        symbolRadius: 0,  // Square shape
        symbolHeight: 12,
        symbolWidth: 12
       },
      exporting: { enabled: false },
      chart: {
        renderTo: 'container',
        height: 200,
        type: 'line',
        backgroundColor: 'Transparent'
      },
      yAxis: {
        visible: true,
        margin: 5,
        labels: {
          //eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: function (this: any): string {
            return `${round(this.value, 3)} ${that.seriesOptions.unit}`;
          }
        },
        title: {
          text: null
        },
        minTickInterval: 0.001
      },
      xAxis: {
        visible: true,
        type: 'datetime',
        dateTimeLabelFormats: {
          minute: {
            main: '%H:%M'
          },
          hour: {
            main: '%H:%M'
          },
          day: {
            main: '%e. %b'
          },
          week: {
            main: '%e. %b'
          },
          month: {
            main: "%b '%y"
          }
        }
      },
      tooltip: {
        outside: true,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        shadow: false,
        useHTML: true,
        style: {
          padding: '0',
          fontFamily: 'Roboto'
        },
        shared: false
      },
      lang: {
        noData: '<div class="load-msg">' + noDataMsg + '</div>'
      }
    };
    return chartOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getSeriesData(resultData: Array<any>, calcSLA = true) {
    this.zones = [];
    this.seriesData = [];
    this.data = [];
    this.thresholdData = [];
    if (resultData?.length) {
      resultData.forEach((item, index) => {
        const timestamp = item.timestamp;
        const val =
          parseFloat(item.data[this.seriesOptions.field]) < 0
            ? 0
            : round(parseFloat(item.data[this.seriesOptions.field]), 3);
        this.data.push({
          x: timestamp,
          y: val,
          name: this.metricsName
        });
        if (
          !this.showCurrentValue &&
          !isNil(this.streamConfig[this.seriesOptions.sla]) &&
          (index === 0 || index + 1 === resultData.length)
        ) {
          this.thresholdData.push({
            x: timestamp,
            y: this.streamConfig[this.seriesOptions.sla],
            name: this.metricsName + ' Threshold'
          });
        }
      });

      if (this.streamConfig[this.seriesOptions.sla]) {
        this.zones.push(
          {
            value: this.streamConfig[this.seriesOptions.sla],
            color: HighchartsSeverity.OK,
            className: 'line-width-small',
            thresholdReached: false
          },
          {
            color: HighchartsSeverity.CRITICAL,
            className: 'line-width-small',
            thresholdReached: true
          }
        );
      }

      this.seriesData.push({
        id: this.streamConfig[this.seriesOptions.sla],
        data: this.data,
        zones: this.zones
      });
      this._handleGap(this.seriesData[0]);
      if (!this.showCurrentValue && !isNil(this.streamConfig[this.seriesOptions.sla])) {
        this.seriesData.push({
          id: 'threshold',
          data: this.thresholdData,
          type: 'area',
          color: '#87c93c',
          visible: false,
          name: this.metricsName + ' Threshold',
          fillOpacity: 0.2
        });
      }
    }
  }

  // eslint-disable-next-line
  public _handleGap(item: any, granularity = 1): void {
    if (item && item.data && item.data.length) {
      const result = [];
      // Stored first data in result and iterate from second value
      result.push(item.data[0]);
      for (let i = 1; i < item.data.length; i++) {
        const prev = item.data[i - 1];
        const cur = item.data[i];
        if (cur.x - prev.x > granularity * this.aggregationRange * this.maxAllowedGap) {
          result.push({ x: prev.x + 1, y: null });
        }
        result.push(cur);
      }
      item.data = result;
    }
  }

  // Method to check if metricsCurrentValue is valid
  isMetricsCurrentValueValidAndNotNaN(): boolean {
    return !this.data.every(data => isNaN(data.y));
  }
  /**
   * get the x-axis zone color. Compare the real value with the historic value to see if they have crossed the
   * warning or critical thresholds. Threshold numbers are given in percentage.
   * @param {*} rvalue - the real value to compare
   * @param {*} histValue - the historic value to compare against
   * @param {*} defaultColor - if the thresholds are not crossed then the default color for the zone
   */

  calculateZoneColor(rvalue, histValue, defaultColor, reverseSLA) {
    if (reverseSLA) {
      return rvalue < histValue ? HighchartsSeverity.CRITICAL : defaultColor;
    }

    return rvalue > histValue ? HighchartsSeverity.CRITICAL : defaultColor;
  }
}
